var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { provideFluentDesignSystem } from "@fluentui/web-components";
import { FASTElement, css, customElement, html, observable, when } from "@microsoft/fast-element";
import { Measures } from "../../../../telemetry/Measures";
import { dashboard } from "../../../../telemetry/TraceLogging";
import { getHostInstanceManagerApi } from "../../../externalApi/InstanceManagerApi";
import { addSystemThemeChangedEventListener, removeSystemThemeChangedEventListener } from "../../../externalApi/ThemeApi";
import { isFeatureEnabled } from "../../../externalApi/VelocityApi";
import { getResources } from "../../../getResources";
import { formatString } from "../../../utils/stringUtilities";
import { addWidgetVisibilityEventListener, getWidgetElementAsync, isRiverInstance, onWidgetRemovedFromRiver, removeWidgetVisibilityEventListener } from "../../../webUXApi/widgetApi";
import widgetApiVersion from "../../../widgetRuntime/apis/api.interfaces";
import { notifyWidgetProvider } from "../../AdaptiveCardWidget/AdaptiveCardRenderingAndActions";
import { getLocalAdaptiveCard } from "../../AdaptiveCardWidget/LocalAdaptiveCardUI";
import { getAdaptiveCard, updateHostContext } from "../../AdaptiveCardWidget/AdaptiveCardWidget";
import { widgetActionMenu } from "./action-menu";
import { widgetErrorCard } from "./error-card";
import { widgetTitleBar } from "./title-bar";
/**
 * Register with the Fluent Design System.
 */
export const FluentDesignSystem = provideFluentDesignSystem().register(widgetTitleBar(), widgetActionMenu(), widgetErrorCard());
export const widgetSizes = ["small", "medium", "large"];
/* eslint-disable sort-keys */
export const widgetDimensions = {
    small: {
        height: "146px",
        width: "300px",
    },
    medium: {
        height: "304px",
        width: "300px",
    },
    large: {
        height: "462px",
        width: "300px",
    },
};
// template must explicitly check for size change so that error card template will update itself
// Outlook and ToDo widgets Iframe is not propogating DnD events causing a sticky behaviour when DnD is performed on these widgets.
// To get around it, we are stacking title-bar above the Iframe so that events are dispacted correctly.
// Correct fix should be from outlook to propoagate these events.  https://task.ms/48065272
const template = html `
    <div class="outer" tabindex="1" style="height:${(x) => x.widgetHeight}">
        <link rel="stylesheet" href="https://static2.sharepointonline.com/files/fabric/office-ui-fabric-core/9.6.1/css/fabric.min.css" />

        ${when((x) => x.ready && x.config.widgetInstance.isOutlookWidget(), html `
                <div class="widget-title-bar" style="z-index: 3;">
                    <fluent-widget-title-bar
                        :widgetInstance="${(x) => x.config.widgetInstance}"
                        :loading="${(x) => x.loadingAction}"
                        :error="${(x) => x.errorAction}"
                        :lastUpdated="${(x) => x.lastUpdatedStatusString}"
                    ></fluent-widget-title-bar>
                </div>
            `)}
        ${when((x) => x.ready && !x.config.widgetInstance.isOutlookWidget(), html `
                <div class="widget-title-bar">
                    <fluent-widget-title-bar
                        :widgetInstance="${(x) => x.config.widgetInstance}"
                        :loading="${(x) => x.loadingAction}"
                        :error="${(x) => x.errorAction}"
                        :lastUpdated="${(x) => x.lastUpdatedStatusString}"
                    ></fluent-widget-title-bar>
                </div>
            `)}
        <div class="body" position="absolute">
            ${when((x) => x.widgetState !== "error", html ` <slot></slot> `)}
            ${when((x) => x.widgetState === "error" && x.widgetHeight === widgetDimensions.small.height, html `
                    <fluent-widget-error-card :widgetInstance="${(x) => x.config.widgetInstance}"> </fluent-widget-error-card>
                `)}
            ${when((x) => x.widgetState === "error" && x.widgetHeight !== widgetDimensions.small.height, html `
                    <fluent-widget-error-card :widgetInstance="${(x) => x.config.widgetInstance}"> </fluent-widget-error-card>
                `)}
        </div>
    </div>
`;
const styles = css `
    .outer {
        display: grid;
        grid-template-rows: auto;
        gap: 8px;
        font-family: var(--body-font);
        background-color: var(--fill-color);
        color: var(--neutral-foreground-rest);
        height: 100%;
        border-radius: 8px;
    }
    .outer .widget-title-bar {
        height: fit-content;
        grid-area: 1/1;
        position: relative;
        border-radius: inherit;
        font-size: var(--type-ramp-minus-1-font-size);
            }
    .outer .body {
        height: inherit;
        width: 300px;
        grid-area: 1/1;
        border-radius: inherit;
        content-visibility: auto;
    }
   }
`;
const globalFastInstances = new Map();
const loadingSpinnerTimeoutLength = 500;
export const suspendActionTimeoutLength = 10000; // 10 seconds
export const suspendActionDebounceTimeoutLength = 100; // debounce to prevent double clicks
const adaptiveCardActionState = new Map();
const adaptiveCardActionLoadingCount = new Map();
/* eslint-disable sort-keys */
let WidgetFrameComponent = class WidgetFrameComponent extends FASTElement {
    constructor() {
        super(...arguments);
        this.ready = false;
        this.widgetHeight = widgetDimensions.small.height;
        this.widgetWidth = widgetDimensions.small.width;
        this.loadingAction = false;
        this.errorAction = false;
        this.showStatus = false;
        this.lastUpdatedStatusString = "";
        this.lastUpdatedTime = 0;
        this._firstActivation = true;
        this._signalApi = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("signals", 1, 1);
        this.widgetStateChangeCallback = (instance, previousState, currentState) => {
            if (previousState !== currentState) {
                Measures.logEvent(dashboard, "WidgetStateChanged", 16777216 /* productAndServicePerformance */, {
                    appId: instance.widgetRegistration.appId,
                    currentState: currentState,
                    instanceId: instance.instanceId,
                    errorStateCode: instance.widgetErrorCode,
                    errorStateReason: instance.widgetErrorStateReason,
                    isAdaptiveCard: instance.widgetRegistration.isAdaptiveCard,
                    isFast: true,
                    isReauthRequiredUserEnabled: true,
                    isSignedOutUserEnabled: true,
                    isTombstoned: instance.widgetRegistration.isTombstoned,
                    previousState: previousState,
                    providerId: instance.widgetRegistration.providerId,
                    widgetDefinitionId: instance.widgetDefinitionId,
                    widgetInstanceId: instance.widgetId,
                });
                let stateSignal = "";
                switch (currentState) {
                    case "loading":
                        stateSignal = "Loading";
                        break;
                    case "error":
                        stateSignal = "Error";
                        break;
                    case "normal":
                        stateSignal = "Normal";
                        break;
                }
                if (this._signalApi && stateSignal !== "") {
                    let payload = {
                        widgetItem: {
                            appId: instance.widgetRegistration.appId,
                            isPeregrine: false,
                            isPlatform: instance.isPlatform,
                            providerId: instance.widgetRegistration.providerId,
                            widgetDefinitionId: instance.widgetDefinitionId,
                            widgetInstanceId: instance.widgetId,
                            widgetState: stateSignal,
                        },
                    };
                    let signal = { name: "WidgetStateUpdated", payload: JSON.stringify(payload), signalType: "WDX" };
                    this._signalApi.sendSignal(signal);
                }
                this.widgetState = currentState;
            }
        };
        this.widgetVisibilityChangeCallback = async (eventName) => {
            if (eventName === "visible") {
                if (this.config.widgetInstance.isPlatform
                    && !this.config.widgetInstance.widgetRegistration.isTombstoned) {
                    await notifyWidgetProvider(this.config.widgetInstance, { "eventKind": "visibility", "visibilityKind": "visible" });
                    // Activate the instance
                    let instanceManagerApi = await getHostInstanceManagerApi();
                    if (this.config.widgetInstance.widgetState !== "normal") {
                        // If the widget is not currently in the normal state, indicate first activation even if it had already been activated before
                        // This will force notification of new payload even if the host does not think it is new.
                        if (!this._firstActivation) {
                            Measures.logEvent(dashboard, "WidgetRecoveryOnActivation", 16777216 /* productAndServicePerformance */, {
                                appId: this.config.widgetInstance.widgetRegistration.appId,
                                instanceId: this.config.widgetInstance.instanceId,
                                widgetState: this.config.widgetInstance.widgetState,
                                errorStateCode: this.config.widgetInstance.widgetErrorCode,
                                errorStateReason: this.config.widgetInstance.widgetErrorStateReason,
                                providerId: this.config.widgetInstance.widgetRegistration.providerId,
                                widgetDefinitionId: this.config.widgetInstance.widgetDefinitionId,
                                widgetInstanceId: this.config.widgetInstance.widgetId,
                            });
                        }
                        this._firstActivation = true;
                    }
                    await (instanceManagerApi === null || instanceManagerApi === void 0 ? void 0 : instanceManagerApi.activateWidget(this.config.widgetInstance.widgetId, this._firstActivation));
                    this._firstActivation = false;
                }
                // Send WebXT telemetry
                const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
                const telemetryApi = widgetModelApiFactory.tryGetApi("telemetry", 1, 1);
                if (telemetryApi) {
                    telemetryApi.sendWebXTWidgetVisibilityTelemetry(this.config.widgetInstance);
                }
            }
            else if (eventName === "notvisible") {
                if (this.config.widgetInstance.isPlatform
                    && !this.config.widgetInstance.widgetRegistration.isTombstoned) {
                    await notifyWidgetProvider(this.config.widgetInstance, { "eventKind": "visibility", "visibilityKind": "hidden" });
                    // Deactivate the instance
                    let instanceManagerApi = await getHostInstanceManagerApi();
                    await (instanceManagerApi === null || instanceManagerApi === void 0 ? void 0 : instanceManagerApi.deactivateWidget(this.config.widgetInstance.widgetId));
                }
            }
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.config.widgetInstance.addStateChangeListener(this.widgetStateChangeCallback);
        addWidgetVisibilityEventListener(this.config.widgetInstance, this.widgetVisibilityChangeCallback);
        // Add the widget content into the Frame when we are connected to the DOM
        getWidgetElementAsync(this.config.widgetInstance).then(async (widgetElement) => {
            this.appendChild(widgetElement);
            await this.config.widgetInstance.widgetInitializationPromise();
            if (this.config.widgetInstance) {
                const widgetSizeApi = this.config.widgetInstance.tryGetApi("sizes", 1, 1);
                if (widgetSizeApi) {
                    this.widgetHeight = widgetDimensions[widgetSizeApi.getSize()].height;
                    this.widgetWidth = widgetDimensions[widgetSizeApi.getSize()].width;
                    widgetSizeApi.addEventListener((eventName, size) => this.onSizeChanged(eventName, size, widgetSizeApi));
                }
                if (this.config.widgetInstance.widgetRegistration.settingsAvailable) {
                    const widgetSettingsApi = this.config.widgetInstance.tryGetApi("settings", 1, 1);
                    if (widgetSettingsApi) {
                        widgetSettingsApi.addEventListener((eventName) => this.onSettingsChanged(eventName));
                    }
                }
                if (this.config.widgetInstance.widgetRegistration.isAdaptiveCard
                    || this.config.widgetInstance.widgetRegistration.isTombstoned) {
                    // Do not render platform widgets here, only render on update
                    adaptiveCardActionState.set(this.config.widgetInstance.widgetInstanceId, "normal");
                    this.lastUpdatedTime = Date.now();
                    // All re-render will occur in the WidgetLifecycleEvent "contentrefresh"
                    this.config.widgetInstance.addSubmitActionListener(this.onACExecuteAction);
                    this.config.widgetInstance.addExecuteActionListener(this.onACExecuteAction);
                    addSystemThemeChangedEventListener(() => {
                        this.onThemeChanged();
                    });
                }
                if (this.config.widgetInstance.widgetRegistration.isAdaptiveCard) {
                    const lifecycleApi = this.config.widgetInstance.tryGetApi("lifecycle", 1, widgetApiVersion);
                    if (lifecycleApi) {
                        // Hook the content refresh event so that the AC will re-render upon host nofication that the provider data has changed
                        // or upon background refresh.
                        lifecycleApi.addEventListener(this.onContentRefresh.bind(this));
                    }
                }
                if (isFeatureEnabled("Feature_AuthApiV5")) {
                    if (this.config.widgetInstance.isOutlookWidget()) {
                        const statusApi = this.config.widgetInstance.tryGetApi("status", 1, widgetApiVersion);
                        if (statusApi) {
                            statusApi.addStatusEventListenerInternal((eventName) => { this.onHandleSignInEvent(eventName); });
                        }
                    }
                }
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.config.widgetInstance.removeStateChangeListener(this.widgetStateChangeCallback);
        removeSystemThemeChangedEventListener(this.onThemeChanged);
        removeWidgetVisibilityEventListener(this.config.widgetInstance, this.widgetVisibilityChangeCallback);
        const widgetSizeApi = this.config.widgetInstance.tryGetApi("sizes", 1, 1);
        if (widgetSizeApi) {
            widgetSizeApi.removeEventListener((eventName, size) => this.onSizeChanged(eventName, size, widgetSizeApi));
        }
        if (this.config.widgetInstance.widgetRegistration.settingsAvailable) {
            const widgetSettingsApi = this.config.widgetInstance.tryGetApi("settings", 1, 1);
            if (widgetSettingsApi) {
                widgetSettingsApi.removeEventListener((eventName) => this.onSettingsChanged(eventName));
            }
        }
        if (this.config.widgetInstance.widgetRegistration.isAdaptiveCard
            || this.config.widgetInstance.widgetRegistration.isTombstoned) {
            this.config.widgetInstance.removeExecuteActionListener(this.onACExecuteAction);
            this.config.widgetInstance.removeSubmitActionListener(this.onACExecuteAction);
        }
        if (this.config.widgetInstance.widgetRegistration.isAdaptiveCard) {
            const lifecycleApi = this.config.widgetInstance.tryGetApi("lifecycle", 1, widgetApiVersion);
            if (lifecycleApi) {
                lifecycleApi.removeEventListener(this.onContentRefresh.bind(this));
            }
        }
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            if (this.config.widgetInstance.isOutlookWidget()) {
                const statusApi = this.config.widgetInstance.tryGetApi("status", 1, widgetApiVersion);
                if (statusApi) {
                    statusApi.removeStatusEventListenerInternal((eventName) => { this.onHandleSignInEvent(eventName); });
                }
            }
        }
        // If this is an unpinned river widget it should be destroyed by onWidgetRemovedFromRiver eventually at widgetInstanceManager.removeWidgetInstanceFromRiver
        // This disconnect may occur after a river widget has been pinned and no longer in the river.
        // This is duplicated code to make it clear that this is boards lite behavior only
        // Do not delete outlook widget instances but let them be reused by the render api
        if (isFeatureEnabled("Feature_BoardsLite") && !this.config.widgetInstance.isOutlookWidget()) {
            isRiverInstance(this.config.widgetInstance).then((isInRiver) => {
                if (isInRiver) {
                    onWidgetRemovedFromRiver(this.config.widgetInstance, false /*hide*/);
                }
            });
        }
        else {
            isRiverInstance(this.config.widgetInstance).then((isInRiver) => {
                if (isInRiver) {
                    onWidgetRemovedFromRiver(this.config.widgetInstance, false /*hide*/);
                }
            });
        }
        Measures.logEvent(dashboard, "WidgetFrameDisconnected", 16777216 /* productAndServicePerformance */, {
            appId: this.config.widgetInstance.widgetRegistration.appId,
            instanceId: this.config.widgetInstance.instanceId,
            errorStateCode: this.config.widgetInstance.widgetErrorCode,
            errorStateReason: this.config.widgetInstance.widgetErrorStateReason,
            isPlatform: this.config.widgetInstance.isPlatform,
            providerId: this.config.widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: this.config.widgetInstance.widgetDefinitionId,
            widgetInstanceId: this.config.widgetInstance.widgetId,
        });
    }
    loadConfig(config) {
        this.config = config;
        this.ready = true;
    }
    async onContentRefresh(eventName) {
        let actionState = adaptiveCardActionState.get(this.config.widgetInstance.widgetInstanceId);
        if (actionState === "loading" || actionState === "error") {
            this.loadingAction = false;
            this.errorAction = false;
            this.showStatus = false;
            this.lastUpdatedStatusString = this.getStatus();
            adaptiveCardActionState.set(this.config.widgetInstance.widgetInstanceId, "normal");
            if (actionState === "loading") {
                Measures.logEvent(dashboard, "AdaptiveCardOnPlatformActionLoadingToNormal", 16777216 /* productAndServicePerformance */, {
                    appId: this.config.widgetInstance.widgetRegistration.appId,
                    providerId: this.config.widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: this.config.widgetInstance.widgetDefinitionId,
                    widgetInstanceId: this.config.widgetInstance.widgetInstanceId,
                });
            }
            else if (actionState === "error") {
                Measures.logEvent(dashboard, "AdaptiveCardOnPlatformActionErrorToNormal", 16777216 /* productAndServicePerformance */, {
                    appId: this.config.widgetInstance.widgetRegistration.appId,
                    providerId: this.config.widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: this.config.widgetInstance.widgetDefinitionId,
                    widgetInstanceId: this.config.widgetInstance.widgetInstanceId,
                });
            }
        }
        this.lastUpdatedTime = Date.now();
        if (eventName === "contentrefresh") {
            await this.onACContentRefresh();
        }
    }
    getStatus() {
        if (this.showStatus) {
            // If last updated duration is < 1hr, show "Xm ago"
            // If last updated duration is >= 1hr, show "Xh ago"
            let lastUpdatedDurationMins = Math.floor((Date.now() - this.lastUpdatedTime.valueOf()) / (60 * 1000));
            return `${lastUpdatedDurationMins < 60
                ? formatString(getResources().WidgetStatusApi_lastUpdatedDuration_Min, {
                    duration: new Intl.NumberFormat().format(lastUpdatedDurationMins),
                })
                : formatString(getResources().WidgetStatusApi_lastUpdatedDuration_Hour, {
                    duration: new Intl.NumberFormat().format(Math.floor(lastUpdatedDurationMins / 60)),
                })} `;
        }
        return "";
    }
    async onACExecuteAction(actionProps, instanceId, widgetInstanceId, widgetRegistration) {
        const frame = globalFastInstances.get(instanceId);
        if (frame) {
            if (widgetRegistration.entryPoint.type === "PLATFORM") {
                Measures.logEvent(dashboard, "AdaptiveCardOnPlatformActionHandler", 16777216 /* productAndServicePerformance */, {
                    appId: widgetRegistration.appId,
                    providerId: widgetRegistration.providerId,
                    widgetDefinitionId: widgetRegistration.id,
                    widgetInstanceId: widgetInstanceId,
                });
                adaptiveCardActionState.set(widgetInstanceId, "loading");
                if (adaptiveCardActionLoadingCount.has(widgetInstanceId)) {
                    adaptiveCardActionLoadingCount.set(widgetInstanceId, adaptiveCardActionLoadingCount.get(widgetInstanceId) + 1);
                }
                else {
                    // First time seeing an action so init to 1.
                    adaptiveCardActionLoadingCount.set(widgetInstanceId, 1);
                }
                setTimeout(() => {
                    if (adaptiveCardActionState.get(widgetInstanceId) === "loading") {
                        frame.loadingAction = true; // Start spinner
                        Measures.logEvent(dashboard, "AdaptiveCardOnPlatformActionSpinnerActivated", 16777216 /* productAndServicePerformance */, {
                            appId: widgetRegistration.appId,
                            providerId: widgetRegistration.providerId,
                            widgetDefinitionId: widgetRegistration.id,
                            widgetInstanceId: widgetInstanceId,
                        });
                    }
                }, loadingSpinnerTimeoutLength);
                setTimeout(() => {
                    if (adaptiveCardActionLoadingCount.has(widgetInstanceId)) {
                        adaptiveCardActionLoadingCount.set(widgetInstanceId, adaptiveCardActionLoadingCount.get(widgetInstanceId) - 1);
                    }
                    else {
                        // This should be impossible but just in case...
                        adaptiveCardActionLoadingCount.set(widgetInstanceId, 0);
                    }
                    // Stop the spinner and show error status as long as there's no more actions to wait on.
                    // If there are other actions loading, they could still succeed so don't do anything.
                    if ((adaptiveCardActionState.get(widgetInstanceId) === "loading") &&
                        (adaptiveCardActionLoadingCount.get(widgetInstanceId) === 0)) {
                        frame.loadingAction = false;
                        frame.errorAction = true;
                        frame.showStatus = true;
                        frame.lastUpdatedStatusString = frame.getStatus();
                        adaptiveCardActionState.set(widgetInstanceId, "error");
                        Measures.logEvent(dashboard, "AdaptiveCardOnPlatformActionErrorActivated", 16777216 /* productAndServicePerformance */, {
                            appId: widgetRegistration.appId,
                            providerId: widgetRegistration.providerId,
                            widgetDefinitionId: widgetRegistration.id,
                            widgetInstanceId: widgetInstanceId,
                        });
                    }
                }, suspendActionTimeoutLength);
            }
        }
    }
    async onOutlookSignInButtonClicked(actionProps, instanceId, widgetInstanceId, widgetRegistration) {
        const frame = globalFastInstances.get(instanceId);
        if (frame) {
            const statusApi = frame.config.widgetInstance.tryGetApi("status", 1, widgetApiVersion);
            if (statusApi) {
                statusApi.outlookSignInButtonCallback();
            }
        }
        Measures.logEvent(dashboard, "OutlookSignInButtonClicked", 16777216 /* productAndServicePerformance */, {
            actionProps: actionProps,
            appId: widgetRegistration.appId,
            isFrameValid: frame !== undefined,
            providerId: widgetRegistration.providerId,
            widgetDefinitionId: widgetRegistration.id,
            widgetInstanceId: widgetInstanceId,
        });
    }
    async onACContentRefresh() {
        let isTombstoned = this.config.widgetInstance.widgetRegistration.isTombstoned;
        // Do not render new content if tombstoned
        if (!isTombstoned) {
            await this.renderAdaptiveCard(new Array());
        }
    }
    // Will re-render widgets onACContentRefresh() after onPlatformWidgetRegistrationUpdated()
    async onThemeChanged() {
        await this.updateACHostParams("hostTheme");
    }
    async updateACHostParams(property, value) {
        let shouldReRender = await updateHostContext(this.config.widgetInstance, property, value);
        let isTombstoned = this.config.widgetInstance.widgetRegistration.isTombstoned;
        if (shouldReRender) {
            if (isTombstoned) {
                await this.renderTombstonedAdaptiveCard();
            }
            else if (this.isOutlookSignedOut()) {
                await this.renderSignedOutAdaptiveCard();
            }
            else {
                await this.renderAdaptiveCard();
            }
        }
    }
    async renderAdaptiveCard(actionProps) {
        Measures.logEvent(dashboard, "AdaptiveCardGetAndRenderStarted", 16777216 /* productAndServicePerformance */, {
            actionProps: actionProps === null || actionProps === void 0 ? void 0 : actionProps.toString(),
            appId: this.config.widgetInstance.widgetRegistration.appId,
            providerId: this.config.widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: this.config.widgetInstance.widgetRegistration.id,
            widgetInstanceId: this.config.widgetInstance.widgetId,
        });
        let widgetElement = await getWidgetElementAsync(this.config.widgetInstance);
        const [card, contentType, errorType] = await getAdaptiveCard(this.config.widgetInstance, this.widgetHeight, this.widgetWidth, actionProps);
        // If card undefined but instance has cached content, will continue to show old content.
        // Otherwise, no content to render and must show error card.
        let isErrorCard = false;
        let clearedErrorState = false;
        if (card === undefined && contentType !== "cachedContent" && this.config.widgetInstance.widgetState !== "error") {
            // If this is not a platform widget that doesn't have content yet we should stay at the placeholder.
            if (!this.config.widgetInstance.isPlatform) {
                this.config.widgetInstance.widgetErrorStateReason = errorType;
                this.config.widgetInstance.widgetErrorCode = 0x8000ffff; // E_UNEXPECTED
                this.config.widgetInstance.widgetState = "error";
                isErrorCard = true;
            }
        }
        else if (card !== undefined) {
            // New data/template to render
            widgetElement.innerHTML = "";
            widgetElement.appendChild(card);
            widgetElement.isAdaptiveCardRendered = true;
            // If new content arrives ensure that we transition to the normal state
            if (this.config.widgetInstance.widgetState === "error") {
                clearedErrorState = true;
                this.config.widgetInstance.widgetErrorStateReason = "";
                this.config.widgetInstance.widgetErrorCode = 0;
                this.config.widgetInstance.widgetState = "normal";
            }
        }
        Measures.logEvent(dashboard, "AdaptiveCardRenderFinished", 16777216 /* productAndServicePerformance */, {
            actionProps: actionProps === null || actionProps === void 0 ? void 0 : actionProps.toString(),
            appId: this.config.widgetInstance.widgetRegistration.appId,
            clearedErrorState: clearedErrorState,
            contentType: contentType,
            errorType: errorType,
            isErrorCard: isErrorCard,
            isPlatform: this.config.widgetInstance.isPlatform,
            providerId: this.config.widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: this.config.widgetInstance.widgetRegistration.id,
            widgetInstanceId: this.config.widgetInstance.widgetId,
        });
    }
    onSizeChanged(eventName, size, widgetSizeApi) {
        if (eventName === "onSizeChangeEnd") {
            this.widgetHeight = widgetDimensions[size].height;
            this.widgetWidth = widgetDimensions[size].width;
            if (this.config.widgetInstance.widgetRegistration.isAdaptiveCard
                || this.config.widgetInstance.widgetRegistration.isTombstoned
                || this.isOutlookSignedOut()) {
                this.updateACHostParams("widgetSize", widgetSizeApi.getSize());
                notifyWidgetProvider(this.config.widgetInstance, { size: widgetSizeApi.getSize() });
            }
        }
    }
    onSettingsChanged(eventName) {
        if (eventName === "onShowSettingsEnd") {
            this.updateACHostParams("isSettingsPayload", "true");
            // "custom" notification will be sent for menu items, currently only 'settings' is supported
            // but in the future the "settings" string may be a value from the provider's manifest.
            notifyWidgetProvider(this.config.widgetInstance, { "custom": "settings" });
        }
    }
    async renderTombstonedAdaptiveCard() {
        const isFeatureDMAEdgeUninstallEnabled = isFeatureEnabled("Feature_DMAEdgeUninstall");
        if (isFeatureDMAEdgeUninstallEnabled) {
            let tombstoneWidgetElement = await getWidgetElementAsync(this.config.widgetInstance);
            const [card, cardError, dataError] = await getLocalAdaptiveCard(this.config.widgetInstance, this.widgetHeight, this.widgetWidth);
            tombstoneWidgetElement.innerHTML = "";
            tombstoneWidgetElement.appendChild(card);
            tombstoneWidgetElement.isAdaptiveCardRendered = true;
            this.logLocalCardRenderFinished(true, cardError, dataError);
        }
    }
    isOutlookSignedOut() {
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            return this._isUserSignedIn === false && this.config.widgetInstance.isOutlookWidget();
        }
        return false;
    }
    onHandleSignInEvent(eventName) {
        if (eventName === "onSignedIn") {
            this._isUserSignedIn = true;
            this.cleanupOutlookWidgetFrame();
        }
        else if (eventName == "onSignedOut") {
            this._isUserSignedIn = false;
            this.renderSignedOutAdaptiveCard();
        }
    }
    async cleanupOutlookWidgetFrame() {
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            this.config.widgetInstance.removeSubmitActionListener(this.onOutlookSignInButtonClicked);
            removeSystemThemeChangedEventListener(() => { this.onThemeChanged(); });
        }
    }
    async renderSignedOutAdaptiveCard() {
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            let outlookSignedOutElement = await getWidgetElementAsync(this.config.widgetInstance);
            const [card, cardError, dataError] = await getLocalAdaptiveCard(this.config.widgetInstance, this.widgetHeight, this.widgetWidth);
            outlookSignedOutElement.innerHTML = "";
            outlookSignedOutElement.appendChild(card);
            outlookSignedOutElement.isAdaptiveCardRendered = true;
            this.config.widgetInstance.addSubmitActionListener(this.onOutlookSignInButtonClicked);
            addSystemThemeChangedEventListener(() => {
                this.onThemeChanged();
            });
            this.logLocalCardRenderFinished(false, cardError, dataError);
        }
    }
    logLocalCardRenderFinished(isTombstoned, cardError, dataError) {
        Measures.logEvent(dashboard, "LocalAdaptiveCardRenderFinished", 16777216 /* productAndServicePerformance */, {
            appId: this.config.widgetInstance.widgetRegistration.appId,
            cardCreationFailure: cardError,
            dataError: dataError,
            isTombstoned: isTombstoned,
            isUserSignedIn: this._isUserSignedIn,
            providerId: this.config.widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: this.config.widgetInstance.widgetRegistration.id,
            widgetInstanceId: this.config.widgetInstance.widgetId,
        });
    }
};
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "config", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "ready", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "widgetHeight", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "widgetWidth", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "loadingAction", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "errorAction", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "showStatus", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "lastUpdatedStatusString", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], WidgetFrameComponent.prototype, "lastUpdatedTime", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], WidgetFrameComponent.prototype, "widgetState", void 0);
WidgetFrameComponent = __decorate([
    customElement({ name: "widget-frame-component", template, styles })
], WidgetFrameComponent);
function wrapWidget(destination, config) {
    // If we already have a frame created for the widget, re-use it
    if (globalFastInstances.has(config.widgetInstance.instanceId)) {
        destination.appendChild(globalFastInstances.get(config.widgetInstance.instanceId));
    }
    else {
        const frame = new WidgetFrameComponent();
        frame.loadConfig(config);
        globalFastInstances.set(config.widgetInstance.instanceId, frame);
        destination.appendChild(frame);
    }
}
export { wrapWidget };
